import { SKIP_ARGUMENTS } from '../../components/AutomationStudio/constant';

/** Editable step fields compared for dirty highlights (excludes runtime/execution chrome). */
export const STEP_TRACKED_FIELDS = Object.freeze([
    'description',
    'tool',
    'executionMode',
    'args',
    'selectors',
    'executionStrategies',
    'expectedOutcome',
    'assertionCode',
    'useAssertionCode',
]);

function stableStringify(value) {
    if (value === undefined) return undefined;
    return JSON.stringify(value, (_, v) => {
        if (v && typeof v === 'object' && !Array.isArray(v)) {
            return Object.keys(v)
                .sort()
                .reduce((acc, key) => {
                    acc[key] = v[key];
                    return acc;
                }, {});
        }
        return v;
    });
}

function normalizeDescription(step = {}) {
    return String(
        step.description ??
            step.stepDescription ??
            step.extractedData?.stepDescription ??
            step.args?.stepDescription ??
            ''
    ).trim();
}

function normalizeExpectedOutcome(step = {}) {
    return String(
        step.expectedOutcome ??
            step.expected_outcome ??
            step.args?.expectedOutcome ??
            step.arguments?.expectedOutcome ??
            step.extractedData?.evaluation?.expectedOutcome ??
            ''
    ).trim();
}

function normalizeAssertionCode(step = {}) {
    return String(
        step.playwrightAssertion?.code ??
            step.args?.assertionCode ??
            step.arguments?.assertionCode ??
            step.extractedData?.evaluation?.assertionCode ??
            ''
    ).trim();
}

function normalizeArgs(step = {}) {
    const args = step.args ?? step.arguments ?? {};
    const next = {};
    Object.keys(args)
        .filter((key) => !SKIP_ARGUMENTS.includes(key))
        .sort()
        .forEach((key) => {
            next[key] = args[key];
        });
    return next;
}

function normalizeSelectors(step = {}) {
    const selectors = step.selectors;
    if (!Array.isArray(selectors)) return [];
    return selectors.map((sel) => ({
        type: sel?.type ?? '',
        value: String(sel?.value ?? sel?.selector ?? '').trim(),
    }));
}

function normalizeExecutionStrategies(step = {}) {
    const strategies = Array.isArray(step.executionStrategies) ? step.executionStrategies : [];
    return strategies
        .map((s) => ({
            type: s?.type ?? '',
            code: String(s?.code ?? '').trim(),
            priority: s?.priority ?? 0,
            enabled: s?.enabled !== false,
        }))
        .sort((a, b) => String(a.type).localeCompare(String(b.type)));
}

/** Canonical editable snapshot for one step. */
export function normalizeStepForTracking(step = {}) {
    return {
        description: normalizeDescription(step),
        tool: String(step.tool ?? '').trim(),
        executionMode: String(step.executionMode ?? '').trim(),
        args: normalizeArgs(step),
        selectors: normalizeSelectors(step),
        executionStrategies: normalizeExecutionStrategies(step),
        expectedOutcome: normalizeExpectedOutcome(step),
        assertionCode: normalizeAssertionCode(step),
        useAssertionCode: normalizeUseAssertionCode(step),
    };
}

/** Matches VerificationTab: assertion-code mode only when explicitly true. */
function normalizeUseAssertionCode(step = {}) {
    return step?.useAssertionCode === true;
}

/** Per-argument dirty map so only the edited argument field highlights. */
function getArgsDirtyByKey(currentStep, baselineStep) {
    const current = normalizeArgs(currentStep);
    const baseline = normalizeArgs(baselineStep ?? {});
    const keys = new Set([...Object.keys(current), ...Object.keys(baseline)]);
    const map = {};
    keys.forEach((key) => {
        map[key] = stableStringify(current[key]) !== stableStringify(baseline[key]);
    });
    return map;
}

/** Compare a single execution strategy's editable content (ignores priority/order). */
function normalizeStrategyContent(strategy = {}) {
    return {
        type: strategy?.type ?? '',
        selector: String(strategy?.selector ?? strategy?.xpath ?? '').trim(),
        code: String(strategy?.code ?? '').trim(),
        enabled: strategy?.enabled !== false,
    };
}

/** Per-strategy dirty map keyed by strategy id — highlights only the edited selector card. */
function getStrategyDirtyById(currentStep, baselineStep) {
    const current = Array.isArray(currentStep?.executionStrategies) ? currentStep.executionStrategies : [];
    const baseline = Array.isArray(baselineStep?.executionStrategies) ? baselineStep.executionStrategies : [];
    const baseById = new Map();
    baseline.forEach((s) => {
        if (s?.id != null) baseById.set(String(s.id), s);
    });
    const map = {};
    current.forEach((s) => {
        if (s?.id == null) return;
        const b = baseById.get(String(s.id));
        map[String(s.id)] =
            !b ||
            stableStringify(normalizeStrategyContent(s)) !== stableStringify(normalizeStrategyContent(b));
    });
    return map;
}

function getPlaywrightCode(step = {}) {
    const list = Array.isArray(step?.executionStrategies) ? step.executionStrategies : [];
    const s = list.find((x) => x?.type === 'playwright_code');
    return String(s?.code ?? '').trim();
}

export function getStepFieldDirtyMap(currentStep, baselineStep) {
    const current = normalizeStepForTracking(currentStep);
    const baseline = normalizeStepForTracking(baselineStep ?? {});
    const dirty = {};
    STEP_TRACKED_FIELDS.forEach((field) => {
        dirty[field] = stableStringify(current[field]) !== stableStringify(baseline[field]);
    });
    // Granular sub-maps so only the specific edited control highlights (not siblings).
    dirty.argsByKey = getArgsDirtyByKey(currentStep, baselineStep);
    dirty.strategyDirtyById = getStrategyDirtyById(currentStep, baselineStep);
    dirty.playwrightCode = getPlaywrightCode(currentStep) !== getPlaywrightCode(baselineStep ?? {});
    return dirty;
}

export function isStepDirty(currentStep, baselineStep) {
    if (!baselineStep) return true;
    const dirty = getStepFieldDirtyMap(currentStep, baselineStep);
    return STEP_TRACKED_FIELDS.some((field) => dirty[field]);
}

export function isInstructionTextDirty(currentInstruction, baselineInstruction) {
    const current = String(currentInstruction?.text ?? '').trim();
    const baseline = String(baselineInstruction?.text ?? '').trim();
    return current !== baseline;
}

function indexInstructionsById(instructions = []) {
    const map = new Map();
    (instructions || []).forEach((inst) => {
        if (inst?.id != null) map.set(String(inst.id), inst);
    });
    return map;
}

function indexStepsById(instructions = []) {
    const map = new Map();
    (instructions || []).forEach((inst) => {
        (inst.aiSteps ?? []).forEach((step) => {
            if (step?.id != null) map.set(String(step.id), step);
        });
    });
    return map;
}

/**
 * Diff current plan draft against a baseline snapshot.
 * @returns {{ dirtyInstructionIds: string[], dirtyStepIds: string[], fieldDirtyByStepId: Record<string, object> }}
 */
export function diffPlanAgainstBaseline(currentInstructions = [], baselineInstructions = []) {
    const baselineByInstId = indexInstructionsById(baselineInstructions);
    const baselineByStepId = indexStepsById(baselineInstructions);
    const currentByStepId = indexStepsById(currentInstructions);

    const dirtyInstructionIds = [];
    const dirtyStepIds = [];
    const fieldDirtyByStepId = {};

    (currentInstructions || []).forEach((inst) => {
        if (inst?.id == null) return;
        const baselineInst = baselineByInstId.get(String(inst.id));
        if (!baselineInst || isInstructionTextDirty(inst, baselineInst) || inst.removedFromEditor) {
            dirtyInstructionIds.push(String(inst.id));
        }

        (inst.aiSteps ?? []).forEach((step) => {
            if (step?.id == null) return;
            const stepId = String(step.id);
            const baselineStep = baselineByStepId.get(stepId);
            if (!baselineStep) {
                dirtyStepIds.push(stepId);
                // New step: diff against empty baseline so granular sub-maps populate too.
                fieldDirtyByStepId[stepId] = getStepFieldDirtyMap(step, {});
                return;
            }
            const fieldDirty = getStepFieldDirtyMap(step, baselineStep);
            if (STEP_TRACKED_FIELDS.some((field) => fieldDirty[field])) {
                dirtyStepIds.push(stepId);
                fieldDirtyByStepId[stepId] = fieldDirty;
            }
        });
    });

    // Steps removed from draft vs baseline also count as plan dirty (left panel won't show them).
    baselineByStepId.forEach((_, stepId) => {
        if (!currentByStepId.has(stepId) && !dirtyStepIds.includes(stepId)) {
            dirtyStepIds.push(stepId);
        }
    });

    return { dirtyInstructionIds, dirtyStepIds, fieldDirtyByStepId };
}

/** Instruction dirty for chip/highlights. */
export function hasVisibleUnsavedChanges(currentInstructions = [], diff) {
    const dirtyInstrSet = new Set(diff?.dirtyInstructionIds ?? []);
    const hasInstructionDirty = (currentInstructions || []).some(
        (inst) => inst?.id != null && dirtyInstrSet.has(String(inst.id))
    );
    return hasInstructionDirty || (diff?.dirtyStepIds?.length ?? 0) > 0;
}

/** Highlight baseline: prefer session baseline (post-generate/save) over lastSavedSnapshot. */
export function getChangeTrackingBaselineInstructions({
    savedPlan,
    planDraft,
    generationRun,
} = {}) {
    if (planDraft?.changeTrackingBaseline) {
        return planDraft.changeTrackingBaseline;
    }
    if (savedPlan?.lastSavedSnapshot?.instructions) {
        return savedPlan.lastSavedSnapshot.instructions;
    }
    if (generationRun?.completedInstructions?.length) {
        return generationRun.completedInstructions;
    }
    return null;
}

/** Keep isDirty / isSaved aligned with snapshot diff after reversible edits. */
export function syncPlanDirtyFlagsFromBaseline(state) {
    const baseline = getChangeTrackingBaselineInstructions({
        savedPlan: state.savedPlan,
        planDraft: state.planDraft,
        generationRun: state.generationRun,
    });
    if (!baseline) {
        state.planDraft.isDirty = true;
        state.savedPlan.isSaved = false;
        return;
    }
    const diff = diffPlanAgainstBaseline(state.planDraft.instructions, baseline);
    const hasChanges = hasVisibleUnsavedChanges(state.planDraft.instructions, diff);
    state.planDraft.isDirty = hasChanges;
    if (!hasChanges && state.savedPlan?.lastSavedSnapshot?.instructions) {
        state.savedPlan.isSaved = true;
    } else if (hasChanges) {
        state.savedPlan.isSaved = false;
    }
}
